import { useState, useEffect, useCallback } from 'react'
import { Modal } from '../ui/Modal'
import { Avatar } from '../ui/Avatar'
import { Spinner } from '../ui/Spinner'
import { groupsApi } from '../../api/groups'
import { usersApi } from '../../api/users'
import { chatsApi } from '../../api/chats'
import { useUiStore } from '../../stores/uiStore'
import { useChatStore } from '../../stores/chatStore'
import { useAuthStore } from '../../stores/authStore'
import type { Chat, User } from '../../types'

interface CreateGroupModalProps {
  onClose: () => void
}

export function CreateGroupModal({ onClose }: CreateGroupModalProps) {
  const [step, setStep] = useState<'info' | 'members'>('info')
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<User[]>([])
  const [members, setMembers] = useState<User[]>([])
  const [searching, setSearching] = useState(false)
  const [loading, setLoading] = useState(false)
  const { addToast } = useUiStore()
  const { upsertChat, setActiveChat } = useChatStore()
  const { userId } = useAuthStore()

  const search = useCallback(async (q: string) => {
    if (q.length < 2) { setResults([]); return }
    setSearching(true)
    try {
      const res = await usersApi.search(q)
      // Себя исключаем — создатель автоматически становится владельцем группы.
      setResults((res.users ?? []).filter((u) => u.id !== userId))
    } catch {
      setResults([])
    } finally {
      setSearching(false)
    }
  }, [userId])

  useEffect(() => {
    const t = setTimeout(() => search(query), 300)
    return () => clearTimeout(t)
  }, [query, search])

  const toggleMember = (user: User) => {
    setMembers((prev) =>
      prev.some((m) => m.id === user.id)
        ? prev.filter((m) => m.id !== user.id)
        : [...prev, user],
    )
  }

  const submit = async () => {
    if (!name.trim()) return
    setLoading(true)
    try {
      const group = await groupsApi.create({ name: name.trim(), description: description.trim() })
      // Синхронно создаём чат группы в messaging, чтобы группа сразу была в списке
      // и отправка сообщений работала без ожидания Kafka. Создателя бэкенд уже
      // добавил владельцем (и в groups, и в messaging).
      const serverChat = await chatsApi.createGroupChat(group.id).catch(() => null)
      // Добавляем выбранных участников (себя добавлять не нужно — уже владелец).
      // Синхронно дублируем в messaging, чтобы участники сразу видели группу и
      // получали сообщения (не полагаемся на Kafka).
      await Promise.all(
        members.map(async (m) => {
          await groupsApi.addMember(group.id, m.id).catch(() => null)
          await chatsApi.addGroupMember(group.id, m.id).catch(() => null)
        }),
      )
      const groupChat: Chat = {
        id: group.id,
        type: 'group',
        // Полный состав: владелец + добавленные (serverChat на момент создания
        // содержит только владельца, поэтому счётчик показывал бы 1).
        members: [userId, ...members.map((m) => m.id)].filter((id): id is string => !!id),
        last_message: null,
        unread_count: 0,
        is_pinned: false,
        is_muted: false,
        folder: 'inbox',
        created_at: serverChat?.created_at ?? new Date().toISOString(),
        title: group.name,
      }
      upsertChat(groupChat)
      setActiveChat(group.id)
      addToast('success', `Группа «${group.name}» создана`)
      onClose()
    } catch {
      addToast('error', 'Не удалось создать группу')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal onClose={onClose} title="Создать группу">
      {step === 'info' ? (
        <div className="p-4 space-y-4">
          <div>
            <label className="block text-txt-secondary text-xs mb-1.5">Group name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Моя группа"
              autoFocus
              maxLength={128}
              className="w-full bg-bg-input text-txt-primary rounded-xl px-3 py-2.5 text-sm
                         outline-none focus:ring-1 focus:ring-accent placeholder-txt-meta"
            />
          </div>
          <div>
            <label className="block text-txt-secondary text-xs mb-1.5">Description</label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="О группе..."
              maxLength={255}
              className="w-full bg-bg-input text-txt-primary rounded-xl px-3 py-2.5 text-sm
                         outline-none focus:ring-1 focus:ring-accent placeholder-txt-meta"
            />
          </div>
          <button
            onClick={() => setStep('members')}
            disabled={!name.trim()}
            className="w-full bg-accent hover:bg-blue-500 disabled:opacity-50 text-white
                       font-medium rounded-xl py-2.5 transition-colors"
          >
            Next: Add Members
          </button>
        </div>
      ) : (
        <div className="p-3 space-y-2">
          {/* Selected members */}
          {members.length > 0 && (
            <div className="flex flex-wrap gap-2 px-1 pb-2 border-b border-divider">
              {members.map((m) => (
                <button
                  key={m.id}
                  onClick={() => toggleMember(m)}
                  className="flex items-center gap-1.5 bg-accent/15 text-accent rounded-full px-2.5 py-1 text-xs font-medium hover:bg-accent/25 transition-colors"
                >
                  {m.first_name || m.username}
                  <span className="text-accent/60">✕</span>
                </button>
              ))}
            </div>
          )}

          <input
            type="text"
            placeholder="Поиск пользователей..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
            className="w-full bg-bg-input text-txt-primary rounded-xl px-3 py-2.5 text-sm
                       outline-none focus:ring-1 focus:ring-accent placeholder-txt-meta"
          />

          {searching && <div className="flex justify-center py-4"><Spinner /></div>}

          {!searching && query.length >= 2 && results.length === 0 && (
            <p className="text-txt-secondary text-sm text-center py-4">No users found</p>
          )}

          <div className="space-y-0.5 max-h-48 overflow-y-auto">
            {results.map((user) => {
              const selected = members.some((m) => m.id === user.id)
              return (
                <button
                  key={user.id}
                  onClick={() => toggleMember(user)}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl transition-colors text-left
                    ${selected ? 'bg-accent/10' : 'hover:bg-bg-hover'}`}
                >
                  <Avatar name={user.first_name || user.username || '?'} src={user.avatar_url} size={36} />
                  <div className="flex-1 min-w-0">
                    <p className="text-txt-primary text-sm font-medium truncate">
                      {[user.first_name, user.last_name].filter(Boolean).join(' ') || user.username}
                    </p>
                    {user.username && (
                      <p className="text-txt-secondary text-xs">@{user.username}</p>
                    )}
                  </div>
                  {selected && (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#4ea6f5" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 6 9 17l-5-5"/>
                    </svg>
                  )}
                </button>
              )
            })}
          </div>

          <div className="border-t border-divider pt-3 flex gap-2">
            <button
              onClick={() => setStep('info')}
              className="flex-1 bg-bg-input hover:bg-bg-hover text-txt-primary font-medium rounded-xl py-2.5 transition-colors text-sm"
            >
              Back
            </button>
            <button
              onClick={submit}
              disabled={loading}
              className="flex-1 bg-accent hover:bg-blue-500 disabled:opacity-50 text-white
                         font-medium rounded-xl py-2.5 transition-colors flex items-center justify-center gap-2 text-sm"
            >
              {loading ? <Spinner size={16} /> : `Create${members.length ? ` (${members.length + 1})` : ''}`}
            </button>
          </div>
        </div>
      )}
    </Modal>
  )
}
